<template>
    <v-card class="mt-3 fittings-card" >
        <v-toolbar   class="fittings-toolbar">
            <v-toolbar-title >
                Fittinguri
            </v-toolbar-title>

            <v-spacer />

            <v-text-field
                v-model="search"
                class="fittings-search"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                label="Caută fitting"
            />

            <v-btn
                icon
                variant="text"
                :aria-label="isExpanded ? 'Ascunde lista' : 'Afișează lista'"
                @click="isExpanded = !isExpanded"
            >
                <v-icon>{{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
            </v-btn>
        </v-toolbar>

        <v-expand-transition>
            <div v-show="isExpanded">
                <v-card-text class="pt-2">
                    <v-data-table
                        :headers="tableHeaders"
                        :items="filteredPlumbingItems"
                        item-value="fitting"
                        v-model:page="tablePage"
                        :items-per-page="10"
                        class="elevation-1"
                    >
                        <template #top>
                            <v-sheet
                                class="table-actions-bar"
                                color="grey-lighten-5"
                                rounded="lg"
                                border
                            @click.stop
                            >
                                <div class="table-actions-meta">
                                    <v-chip
                                        color="primary"
                                        variant="outlined"
                                        class="mr-2"
                                    >
                                        {{ selectedPlumbingItems.length }} produse cu cantitate
                                    </v-chip>
                                </div>
                                <v-btn
                                    size="small"
                                    variant="text"
                                    color="warning"
                                    prepend-icon="mdi-backup-restore"
                                    :disabled="!hasSelectedQuantities"
                                    @click="resetAllQuantities"
                                >
                                    Resetează toate cantitățile
                                </v-btn>
                            </v-sheet>
                        </template>
                        <template #[`item.productCode`]="{ item }">
                            {{ item.productCode }}
                        </template>
                        <template #[`item.productName`]="{ item }">
                            {{ item.productName }}
                        </template>
                        <template #[`item.quantity`]="{ item }">
                            <v-text-field
                                variant="outlined"
                                density="compact"
                                hide-details
                                type="number"
                                min="0"
                                style="max-width: 130px"
                                v-model.number="item.quantity"
                                @blur="commitQuantityAndRecalculate(item)"
                            />
                        </template>
                        <template #[`item.actions`]="{ item }">
                            <v-tooltip text="Resetează cantitate" location="top">
                                <template #activator="{ props }">
                                    <v-btn
                                        v-bind="props"
                                        icon
                                        size="small"
                                        variant="text"
                                        color="warning"
                                        :disabled="Number(item.quantity) <= 0"
                                        @click="clearRowQuantity(item)"
                                    >
                                        <v-icon size="18">mdi-backup-restore</v-icon>
                                    </v-btn>
                                </template>
                            </v-tooltip>
                        </template>
                    </v-data-table>
                </v-card-text>
            </div>
        </v-expand-transition>
    </v-card>
</template>

<script>
import apiClient from "@/utils/apiClient";
import { getProductSearchScore } from "@/utils/utils";

export default {
    name: "PlumbingSetup",
    data() {
        return {
            plumbingItems: [],
            isExpanded: true,
            search: "",
            tablePage: 1,
            tableHeaders: [
                { title: "Cod produs", key: "productCode", sortable: true },
                { title: "Denumire", key: "productName", sortable: true },
                { title: "Cantitate", key: "quantity", sortable: false },
                { title: "Acțiuni", key: "actions", sortable: false, align: "end" },
            ],
        };
    },
    emits: ["update-selected-plumbing-items", "trigger-recalculation"],
    props: {
        plumbingFittingProducts: {
            type: Array,
            required: true,
        },
        offerId: {
            type: String,
        },
        initialPlumbingItems: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        filteredPlumbingItems() {
            const term = String(this.search || "").trim();
            if (!term) {
                return [...this.plumbingItems].sort((a, b) =>
                    this.sortPlumbingItems(a, b)
                );
            }

            return this.plumbingItems
                .map((item) => ({
                    item,
                    score: getProductSearchScore(
                        item.productName,
                        item.productCode,
                        term
                    ),
                }))
                .filter(({ score }) => score > 0)
                .sort((a, b) => {
                    const quantitySort = this.sortPlumbingItems(a.item, b.item);
                    if (quantitySort !== 0) {
                        return quantitySort;
                    }
                    return b.score - a.score;
                })
                .map(({ item }) => item);
        },
        selectedPlumbingItems() {
            return this.plumbingItems
                .filter((item) => Number(item.quantity) > 0)
                .map((item) => ({
                    fitting: item.fitting,
                    product_code: item.fitting,
                    quantity: Number(item.quantity),
                }));
        },
        hasSelectedQuantities() {
            return this.plumbingItems.some((item) => Number(item.quantity) > 0);
        },
    },
    async mounted() {
        this.initializeRows();

        if (this.offerId) {
            await this.fetchPlumbingSetup(this.offerId);
        }
    },
    methods: {
        sortPlumbingItems(a, b) {
            // Use committed quantity so rows only jump after blur, not while typing
            const aHasQuantity = Number(a.sortQuantity) > 0 ? 1 : 0;
            const bHasQuantity = Number(b.sortQuantity) > 0 ? 1 : 0;
            if (aHasQuantity !== bHasQuantity) {
                return bHasQuantity - aHasQuantity;
            }
            return String(a.productCode || "").localeCompare(
                String(b.productCode || "")
            );
        },
        commitQuantityAndRecalculate(item) {
            if (!item) return;
            const nextQuantity = Number(item.quantity) > 0 ? Number(item.quantity) : 0;
            item.quantity = nextQuantity;
            item.sortQuantity = nextQuantity;
            this.triggerRecalculation();
        },
        normalizeItemsForComparison(items = []) {
            return (Array.isArray(items) ? items : [])
                .map((item) => ({
                    fitting: item?.fitting ?? item?.product_code,
                    quantity: Number(item?.quantity) > 0 ? Number(item.quantity) : 0,
                }))
                .filter((item) => item.fitting)
                .sort((a, b) => String(a.fitting).localeCompare(String(b.fitting)));
        },
        savedItemsMatchCurrentSelection(savedItems = []) {
            const current = this.normalizeItemsForComparison(this.selectedPlumbingItems);
            const incoming = this.normalizeItemsForComparison(savedItems);
            if (current.length !== incoming.length) return false;

            return current.every((item, index) => {
                return (
                    item.fitting === incoming[index].fitting &&
                    item.quantity === incoming[index].quantity
                );
            });
        },
        initializeRows(savedItems = []) {
            const quantityByProductCode = new Map(
                (Array.isArray(savedItems) ? savedItems : []).map((item) => [
                    item?.fitting ?? item?.product_code,
                    Number(item?.quantity) > 0 ? Number(item.quantity) : 0,
                ])
            );

            this.plumbingItems = this.plumbingFittingProducts.map((product) => {
                const quantity =
                    quantityByProductCode.get(product.product_code) ?? 0;
                return {
                    fitting: product.product_code,
                    productCode: product.product_code,
                    productName: product.product_name || product.name || "",
                    quantity,
                    sortQuantity: quantity,
                };
            });
        },
        triggerRecalculation() {
            this.$emit("trigger-recalculation");
        },
        clearRowQuantity(item) {
            if (!item || Number(item.quantity) <= 0) return;
            item.quantity = 0;
            item.sortQuantity = 0;
            this.triggerRecalculation();
        },
        resetAllQuantities() {
            if (!this.hasSelectedQuantities) return;

            this.plumbingItems = this.plumbingItems.map((item) => ({
                ...item,
                quantity: 0,
                sortQuantity: 0,
            }));
            this.triggerRecalculation();
        },
        async fetchPlumbingSetup(offerId) {
            try {
                const response = await apiClient.get(
                    `/api/plumbing-setup/${offerId}/`,
                    { validateStatus: (status) => status === 200 || status === 404 }
                );
                if (response.status === 404) {
                    return;
                }
                const plumbingSetup = response.data?.plumbing_items;
                this.initializeRows(plumbingSetup);
            } catch (error) {
                console.error("Error fetching plumbing setup:", error);
            }
        },
    },
    watch: {
        search() {
            this.tablePage = 1;
        },
        plumbingFittingProducts: {
            immediate: true,
            handler(products) {
                if (!Array.isArray(products) || products.length === 0) return;

                const savedItems = this.plumbingItems
                    .filter((item) => Number(item.quantity) > 0)
                    .map((item) => ({
                        fitting: item.fitting,
                        quantity: item.quantity,
                    }));

                if (this.offerId) {
                    this.initializeRows(
                        savedItems.length > 0
                            ? savedItems
                            : this.initialPlumbingItems
                    );
                    return;
                }

                this.initializeRows(this.initialPlumbingItems);
            },
        },
        initialPlumbingItems: {
            immediate: true,
            handler(newItems) {
                if (this.offerId) return;
                if (this.savedItemsMatchCurrentSelection(newItems)) return;
                this.initializeRows(newItems);
            },
        },
        plumbingItems: {
            handler() {
                this.$emit(
                    "update-selected-plumbing-items",
                    this.selectedPlumbingItems
                );
            },
            deep: true,
        },
    },
};
</script>

<style scoped>
.fittings-toolbar {
    gap: 12px;
    flex-wrap: wrap;
    padding-inline: 12px;
}

.fittings-title {
    flex-shrink: 0;
}

.fittings-search {
    width: 100%;
    max-width: 300px;
}

.table-actions-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 8px 12px 12px;
    padding: 10px 12px;
}

.table-actions-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.6);
}

@media (max-width: 768px) {
    .fittings-search {
        max-width: none;
    }

    .table-actions-bar {
        flex-direction: column;
        align-items: stretch;
    }
}
</style>
